import { type ClipboardEvent, useRef, useState } from 'react'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import { log } from 'utils/logger'
import { extractPastedDesign, hasEmailContent, inspectTemplateHtml } from '../utils/sanitizeTemplateHtml'
import { trackBoxTruckUiInteraction, type TemplateDesignRefusal, type TemplateDesignSource } from '../utils/analytics'

interface UseTemplateDesignPasteParams {
    /** What would stop this design being saved — unsafe HTML, unknown variables. Empty when it can be. */
    issuesFor: (design: string) => string[]
    /** Put a checked, sanitized design into the editor and show it. */
    onApply: (safeDesign: string) => void
}

/**
 * Bringing a whole email design into the template editor: from the AI, from the HTML tab's paste button, or
 * pasted into the Visual tab with the keyboard (which asks first, since it replaces the template).
 *
 * Every path is checked before anything changes. A design that could not be saved is refused with the reason,
 * so the operator never ends up holding a template the Save button will reject.
 */
export const useTemplateDesignPaste = ({ issuesFor, onApply }: UseTemplateDesignPasteParams) => {
    const { showSnackbar } = useSnackbar()
    /** A design pasted into Visual, waiting for the operator to confirm the replacement. */
    const [pendingDesign, setPendingDesign] = useState<string | null>(null)
    // The dialog closes after a confirm too (AlertDialog calls onConfirm, then onClose) — only a close
    // without one is a cancel.
    const confirmedRef = useRef(false)

    const refuse = (reason: TemplateDesignRefusal, message: string): false => {
        trackBoxTruckUiInteraction('template_design_refused', reason)
        showSnackbar(SnackbarType.ERROR, message)
        return false
    }

    const applyDesign = (design: string, source: TemplateDesignSource): boolean => {
        const issues = issuesFor(design)
        if (issues.length > 0) {
            return refuse(
                'unsafe_or_unknown_fields',
                `This design can’t be used: ${issues.slice(0, 2).join('; ')}. Ask the AI to fix that, then paste it again.`
            )
        }
        const safeDesign = inspectTemplateHtml(design).sanitizedHtml
        if (!hasEmailContent(safeDesign)) return refuse('empty', 'The pasted code has no email content in it. Copy the AI’s whole answer, then paste it again.')
        onApply(safeDesign)
        trackBoxTruckUiInteraction('template_design_applied', source)
        showSnackbar(SnackbarType.SUCCESS, 'Design applied. Check the preview below, then click Save template.')
        return true
    }

    // Pasting parses and sanitizes whatever is on the clipboard. None of that is expected to throw, but a throw
    // would otherwise vanish inside an async click handler: nothing happens on screen and nothing is logged.
    const guarded = (paste: () => boolean): boolean => {
        try {
            return paste()
        } catch (error) {
            log.e('Applying a pasted email design failed', error)
            return refuse('unexpected_error', 'We couldn’t read that design. Copy it again, then paste once more.')
        }
    }

    const pasteFrom =
        (source: TemplateDesignSource) =>
        (plainText: string, html: string): boolean =>
            guarded(() => {
                const design = extractPastedDesign(plainText, html)
                if (!design) {
                    return refuse('not_a_design', 'That isn’t an email design. In the AI chat, copy its whole answer with the Copy button, then paste again.')
                }
                return applyDesign(design, source)
            })

    // Caught before the editor sees it: Visual would insert pasted design code as literal text, or nest a pasted
    // email layout inside the current one. Ordinary text and spreadsheet cells are not designs and paste as they always did.
    const handleVisualPaste = (event: ClipboardEvent<HTMLDivElement>) => {
        let design: string | null
        try {
            design = extractPastedDesign(event.clipboardData.getData('text/plain'), event.clipboardData.getData('text/html'))
        } catch (error) {
            // Letting the paste through would put it in Visual flattened or as literal code — block it and say so.
            event.preventDefault()
            event.stopPropagation()
            log.e('Reading a paste into the Visual editor failed', error)
            refuse('unexpected_error', 'We couldn’t read that paste. Copy it again, then paste once more.')
            return
        }
        if (!design) return
        event.preventDefault()
        event.stopPropagation()
        confirmedRef.current = false
        setPendingDesign(design)
    }

    const confirmReplace = () => {
        confirmedRef.current = true
        if (pendingDesign) guarded(() => applyDesign(pendingDesign, 'visual_paste'))
    }

    const closeReplace = () => {
        if (!confirmedRef.current) trackBoxTruckUiInteraction('template_design_replace_cancelled')
        setPendingDesign(null)
    }

    return {
        pasteAiResult: pasteFrom('ai_result'),
        pasteDesignCode: pasteFrom('code_button'),
        handleVisualPaste,
        replaceConfirmOpen: pendingDesign !== null,
        confirmReplace,
        closeReplace,
    }
}
